// src/pages/Dashboard.js

import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

import {
  Modal,
  Input,
  message,
  Spin,
  Segmented,
} from "antd";

import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  HeartIcon,
  UserGroupIcon,
  ScaleIcon,
  DocumentDuplicateIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  CreditCardIcon,
  LockClosedIcon,
  UserPlusIcon,
  PhoneIcon,
  PaperAirplaneIcon,
  UserIcon,
  QrCodeIcon,
  ArrowsRightLeftIcon,
  ShoppingBagIcon,
} from "@heroicons/react/24/outline";

import { useNavigate, useLocation } from "react-router-dom";

import { CHAINS_CONFIG } from "../chains";

import { ethers } from "ethers";
import { QRCodeCanvas } from "qrcode.react";

import baitCard from "../baitpay-card.PNG";
import BaitLogo from "../BaitLogo.png";

import "../components/Walletview.css";

// ==========================================================
// API CONFIGURATION
// ==========================================================

const GOLD_REFERENCE_API =
  "https://goldapi.bait-coin.com/api/gold-reference";

const USERS_API = "https://backend.bait-coin.com/api/users";

const Dashboard = ({
  wallet,
  seedPhrase,
  selectedChain,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // ---------- NEW: app mode toggle ----------
  const [appMode, setAppMode] = useState(
    location.pathname === "/dashboard" ? "BAITCASH" : "BAITPAY"
  );

  // Sync toggle with route changes
  useEffect(() => {
    if (location.pathname === "/dashboard") {
      setAppMode("BAITCASH");
    } else {
      setAppMode("BAITPAY");
    }
  }, [location]);

  // ==========================================================
  // REF FOR DASHBOARD CONTAINER (keep for other modals)
  // ==========================================================
  const dashboardAppRef = useRef(null);

  // ==========================================================
  // BASIC WALLET STATE
  // ==========================================================

  const [nativeBalance, setNativeBalance] = useState(0);
  const [baitTokenBalance, setBaitTokenBalance] = useState(0);
  const [baitTokenBalanceLoading, setBaitTokenBalanceLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);

  const [greeting, setGreeting] = useState("");
  const [currentTime, setCurrentTime] = useState("");

  // ==========================================================
  // BAITCASH ACCOUNT STATE
  // ==========================================================

  const [baitcashAccount, setBaitcashAccount] = useState(null);
  const [baitcashLoading, setBaitcashLoading] = useState(false);
  const [baitcashError, setBaitcashError] = useState("");
  const [showBaitcashInput, setShowBaitcashInput] = useState(false);
  const [baitcashPhone, setBaitcashPhone] = useState("");
  const [baitcashFullName, setBaitcashFullName] = useState("");

  // ==========================================================
  // BAITCASH BALANCE STATE
  // ==========================================================

  const [baitcashBalance, setBaitcashBalance] = useState(0);
  const [baitcashBalanceLoading, setBaitcashBalanceLoading] = useState(false);

  // ==========================================================
  // TRANSACTIONS STATE
  // ==========================================================

  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [showAllTransactions, setShowAllTransactions] = useState(false);

  // ==========================================================
  // RECEIVE PKR MODAL STATE
  // ==========================================================

  const [showReceivePkrModal, setShowReceivePkrModal] = useState(false);
  const [receiveCopied, setReceiveCopied] = useState(false);

  // ==========================================================
  // CONVERSION MODAL STATE (BAIT ↔ PKR)
  // ==========================================================

  const [showConversionModal, setShowConversionModal] = useState(false);
  const [conversionMode, setConversionMode] = useState("baitToPkr");
  const [conversionAmount, setConversionAmount] = useState("");
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionError, setConversionError] = useState("");
  const [conversionStep, setConversionStep] = useState("form");
  const [conversionResult, setConversionResult] = useState(null);

  // ==========================================================
  // EXISTING MODAL STATES
  // ==========================================================

  const [showDonateModal, setShowDonateModal] = useState(false);
  const [donationType, setDonationType] = useState("zakat");
  const [donationAmount, setDonationAmount] = useState("");

  const [showZakatModal, setShowZakatModal] = useState(false);
  const [zakatAmount, setZakatAmount] = useState("");
  const [recipientZakatAddress, setRecipientZakatAddress] = useState("");

  const [showGovtModal, setShowGovtModal] = useState(false);
  const [paymentType, setPaymentType] = useState("tax");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [govtReference, setGovtReference] = useState("");

  const [showWomenModal, setShowWomenModal] = useState(false);
  const [reasonForFunds, setReasonForFunds] = useState("");
  const [profession, setProfession] = useState("");
  const [amount, setAmount] = useState("");
  const [cvFile, setCvFile] = useState(null);

  // ==========================================================
  // PKR / BAIT PRICE STATE
  // ==========================================================

  const [baitPricePkr, setBaitPricePkr] = useState(null);
  const [pkrRateLoading, setPkrRateLoading] = useState(false);

  // ==========================================================
  // GREETING + TIME
  // ==========================================================

  useEffect(() => {
    const hour = new Date().getHours();

    if (hour < 12) {
      setGreeting("Good Morning");
    } else if (hour < 18) {
      setGreeting("Good Afternoon");
    } else {
      setGreeting("Good Evening");
    }

    const updateTime = () => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    };

    updateTime();

    const interval = setInterval(updateTime, 60000);

    return () => clearInterval(interval);
  }, []);

  // ==========================================================
  // ADDRESS FORMATTER
  // ==========================================================

  const formatAddress = (addr) => {
    if (!addr) return "";
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  // ==========================================================
  // BALANCE FORMATTER
  // ==========================================================

  const formatBalance = (bal) => {
    if (bal === undefined || bal === null) {
      return "0.00";
    }

    return bal.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    });
  };

  // ==========================================================
  // FETCH NATIVE BALANCE – ALWAYS USED FOR BAIT
  // ==========================================================

  const fetchNativeBalance = useCallback(async () => {
    if (!wallet || !selectedChain) {
      return;
    }

    try {
      setBalanceLoading(true);

      const chain = CHAINS_CONFIG[selectedChain];

      if (!chain || !chain.rpcUrl) {
        throw new Error("Selected chain RPC not configured");
      }

      const provider = new ethers.JsonRpcProvider(chain.rpcUrl);

      const bal = await provider.getBalance(wallet);
      const nativeBal = Number(ethers.formatEther(bal));

      setNativeBalance(nativeBal);

      // 🔥 For GoldChain, this native balance IS the BAIT balance
      if (selectedChain === "0x777771") {
        setBaitTokenBalance(nativeBal);
        setBaitTokenBalanceLoading(false);
      }
    } catch (err) {
      console.error("Balance fetch error:", err);
    } finally {
      setBalanceLoading(false);
    }
  }, [wallet, selectedChain]);

  useEffect(() => {
    fetchNativeBalance();

    const interval = setInterval(fetchNativeBalance, 1000000);

    return () => clearInterval(interval);
  }, [fetchNativeBalance]);

  // ==========================================================
  // FETCH GOLD REFERENCE / BAIT PKR RATE
  // ==========================================================

  const fetchGoldReference = useCallback(
    async (showLoading = false) => {
      try {
        if (showLoading) {
          setPkrRateLoading(true);
        }

        const response = await fetch(GOLD_REFERENCE_API, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        const data = await response.json();

        if (!data || !data.bait || !data.bait.price_pkr) {
          throw new Error("BAIT PKR price unavailable from API");
        }

        const pkrPrice = Number(data.bait.price_pkr);

        if (!Number.isFinite(pkrPrice) || pkrPrice <= 0) {
          throw new Error("Invalid BAIT PKR price");
        }

        setBaitPricePkr(pkrPrice);
      } catch (err) {
        console.error("Gold reference API error:", err);
      } finally {
        if (showLoading) {
          setPkrRateLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    fetchGoldReference(true);

    const interval = setInterval(() => {
      fetchGoldReference(false);
    }, 300000);

    return () => clearInterval(interval);
  }, [fetchGoldReference]);

  // ==========================================================
  // PKR WALLET BALANCE (calculated from native BAIT)
  // ==========================================================

  const pkrBalance = React.useMemo(() => {
    if (!baitPricePkr || baitPricePkr <= 0) {
      return 0;
    }

    return nativeBalance * baitPricePkr;
  }, [nativeBalance, baitPricePkr]);

  // ==========================================================
  // PKR BALANCE FORMAT
  // ==========================================================

  const formatPkr = (value) => {
    if (!Number.isFinite(Number(value))) {
      return "₨ 0.00";
    }

    return `₨ ${Number(value).toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // ==========================================================
  // FETCH BAITCASH ACCOUNT – ONLY USED FOR PKR AND ACCOUNT INFO
  // ==========================================================

  const fetchBaitcashAccount = useCallback(async () => {
    if (!wallet) {
      setBaitcashAccount(null);
      return;
    }

    try {
      setBaitcashLoading(true);
      setBaitcashError("");

      const response = await fetch(
        `${USERS_API}/wallet/${encodeURIComponent(wallet)}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.status === 404) {
        setBaitcashAccount(null);
        return;
      }

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();

      if (data?.user) {
        const userData = data.user;

        if (userData.balance !== undefined) {
          const balance =
            typeof userData.balance === "number"
              ? userData.balance
              : parseFloat(userData.balance);
          setBaitcashBalance(balance);
        }

        setBaitcashAccount({
          ...userData,
          phoneNumber: userData.phoneNumber,
          walletAddress: userData.walletAddress || wallet,
          fullName: userData.fullName || "User",
          status: "active",
        });
      } else {
        setBaitcashAccount(null);
      }
    } catch (err) {
      console.error("BAITcash account lookup error:", err);
      setBaitcashError("Unable to check BAITcash account");
      setBaitcashAccount(null);
    } finally {
      setBaitcashLoading(false);
    }
  }, [wallet]);

  // ==========================================================
  // FETCH BAITCASH BALANCE (PKR) – called when account exists
  // ==========================================================

  const fetchBaitcashBalance = useCallback(async () => {
    if (!wallet || !baitcashAccount) {
      setBaitcashBalance(0);
      return;
    }

    try {
      setBaitcashBalanceLoading(true);

      const response = await fetch(
        `${USERS_API}/balance/${encodeURIComponent(wallet)}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (response.status === 404) {
        const walletResponse = await fetch(
          `${USERS_API}/wallet/${encodeURIComponent(wallet)}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        if (walletResponse.ok) {
          const walletData = await walletResponse.json();
          if (walletData?.user?.balance !== undefined) {
            const balance =
              typeof walletData.user.balance === "number"
                ? walletData.user.balance
                : parseFloat(walletData.user.balance);
            setBaitcashBalance(balance);
            return;
          }
        }

        setBaitcashBalance(0);
        return;
      }

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const data = await response.json();

      let balance = 0;

      if (data && data.balance !== undefined) {
        balance =
          typeof data.balance === "number"
            ? data.balance
            : parseFloat(data.balance);
      } else if (data && data.user && data.user.balance !== undefined) {
        balance =
          typeof data.user.balance === "number"
            ? data.user.balance
            : parseFloat(data.user.balance);
      }

      setBaitcashBalance(balance);
    } catch (err) {
      console.error("BAITcash balance fetch error:", err);
    } finally {
      setBaitcashBalanceLoading(false);
    }
  }, [wallet, baitcashAccount]);

  // ==========================================================
  // FETCH TRANSACTIONS
  // ==========================================================

  const fetchTransactions = useCallback(async () => {
    if (!wallet) {
      setTransactions([]);
      return;
    }

    try {
      setTransactionsLoading(true);
      const response = await fetch(
        `${USERS_API}/transactions/${encodeURIComponent(wallet)}`,
        {
          method: "GET",
          headers: { Accept: "application/json" },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setTransactions(data.transactions || []);
        }
      }
    } catch (err) {
      console.error("Error fetching transactions:", err);
    } finally {
      setTransactionsLoading(false);
    }
  }, [wallet]);

  // ==========================================================
  // EFFECT: CHECK BAITCASH WHEN WALLET CHANGES
  // ==========================================================

  useEffect(() => {
    fetchBaitcashAccount();
  }, [fetchBaitcashAccount]);

  // ==========================================================
  // EFFECT: FETCH BAITCASH BALANCE (PKR) WHEN ACCOUNT CHANGES
  // ==========================================================

  useEffect(() => {
    if (baitcashAccount) {
      fetchBaitcashBalance();
      fetchTransactions();
    } else {
      setBaitcashBalance(0);
      setTransactions([]);
    }
  }, [baitcashAccount, fetchBaitcashBalance, fetchTransactions]);

  // ==========================================================
  // EFFECT: FETCH BALANCES WHEN CONVERSION MODAL OPENS
  // ==========================================================

  useEffect(() => {
    if (showConversionModal) {
      fetchNativeBalance();
      fetchBaitcashBalance();
      fetchGoldReference(false);
    }
  }, [
    showConversionModal,
    fetchNativeBalance,
    fetchBaitcashBalance,
    fetchGoldReference,
  ]);

  // ==========================================================
  // OPEN SEND PKR PAGE
  // ==========================================================

  const openSendPkrPage = () => {
    navigate("/send-pkr", {
      state: {
        wallet,
        seedPhrase,
        selectedChain,
        baitcashAccount,
        baitcashBalance,
      },
    });
  };

  // ==========================================================
  // ACTIVATE BAITCASH
  // ==========================================================

  const activateBaitcash = async () => {
    if (!wallet) {
      message.error("Wallet is not connected.");
      return;
    }

    const name = baitcashFullName.trim();

    if (!name) {
      setBaitcashError("Please enter your full name.");
      return;
    }

    if (name.length < 2) {
      setBaitcashError("Please enter a valid full name.");
      return;
    }

    const phone = String(baitcashPhone || "").replace(/\D/g, "");

    if (!phone) {
      setBaitcashError("Please enter your Pakistan mobile number.");
      return;
    }

    if (!/^03\d{9}$/.test(phone)) {
      setBaitcashError(
        "Please enter a valid 11-digit Pakistan mobile number, e.g. 03001234567."
      );
      return;
    }

    setBaitcashLoading(true);
    setBaitcashError("");

    try {
      const phoneCheckResponse = await fetch(
        `${USERS_API}/resolve-phone`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            phoneNumber: phone,
          }),
        }
      );

      if (phoneCheckResponse.ok) {
        const phoneData = await phoneCheckResponse.json();
        if (phoneData?.user) {
          if (
            phoneData.user.walletAddress &&
            phoneData.user.walletAddress.toLowerCase() !==
              wallet.toLowerCase()
          ) {
            setBaitcashError(
              `This phone number (${phone}) is already registered with a different wallet address.`
            );
            message.error(
              "Phone number already registered with another wallet."
            );
            setBaitcashLoading(false);
            return;
          }
        }
      }

      const response = await fetch(`${USERS_API}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          fullName: name,
          phoneNumber: phone,
          walletAddress: wallet,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(
          data?.error ||
            data?.details ||
            data?.message ||
            `BAITcash activation failed (${response.status})`
        );
      }

      const account = data?.user || null;

      if (!account) {
        throw new Error(
          "BAITcash was activated but no account information was returned."
        );
      }

      setBaitcashAccount({
        ...account,
        phoneNumber: account.phoneNumber || phone,
        walletAddress: account.walletAddress || wallet,
        fullName: account.fullName || name,
        status: "active",
      });

      setBaitcashPhone("");
      setBaitcashFullName("");
      setShowBaitcashInput(false);

      message.success("BAITcash activated successfully!");
    } catch (err) {
      console.error("BAITcash activation error:", err);

      const errorMsg = err.message || "";
      if (
        errorMsg.toLowerCase().includes("duplicate") ||
        errorMsg.toLowerCase().includes("already registered")
      ) {
        setBaitcashError(
          "This phone number is already registered with another wallet."
        );
      } else {
        setBaitcashError(errorMsg || "Unable to activate BAITcash.");
      }

      message.error(errorMsg || "BAITcash activation failed.");
    } finally {
      setBaitcashLoading(false);
    }
  };

  // ==========================================================
  // RECEIVE MODAL FUNCTIONS
  // ==========================================================

  const resetReceiveModal = () => {
    setReceiveCopied(false);
  };

  // ==========================================================
  // OPEN RECEIVE PKR PAGE
  // ==========================================================

  const openReceivePage = () => {
    if (!baitcashAccount) {
      message.warning("Please activate BAITcash first to receive PKR.");
      return;
    }
    navigate("/receive-pkr", {
      state: {
        wallet,
        baitcashAccount,
      },
    });
  };

  const handleCopyReceiveInfo = () => {
    if (!baitcashAccount?.phoneNumber) return;

    const text = `BAIT Cash: ${baitcashAccount.phoneNumber}`;
    navigator.clipboard.writeText(text);

    setReceiveCopied(true);
    message.success("Phone number copied");

    setTimeout(() => {
      setReceiveCopied(false);
    }, 1500);
  };

  // ==========================================================
  // CONVERSION FUNCTIONS
  // ==========================================================

  const getConvertedAmount = () => {
    const value = parseFloat(conversionAmount);
    if (!value || value <= 0 || !baitPricePkr) return 0;

    if (conversionMode === "baitToPkr") {
      return value * baitPricePkr;
    } else {
      return value / baitPricePkr;
    }
  };

  const convertedAmount = getConvertedAmount();

  const validateConversion = () => {
    if (!baitcashAccount) {
      return "Please activate BAITcash first to perform conversions.";
    }

    if (!conversionAmount || conversionAmount.trim() === "") {
      return "Please enter an amount.";
    }

    const value = parseFloat(conversionAmount);

    if (!Number.isFinite(value) || value <= 0) {
      return "Amount must be greater than zero.";
    }

    if (conversionMode === "baitToPkr") {
      const availableBalance = baitTokenBalance;
      if (value > availableBalance) {
        return `Insufficient BAIT balance. Available: ${availableBalance.toFixed(
          4
        )} BAIT`;
      }
      if (value > 1000000) {
        return "Maximum conversion amount is 1,000,000 BAIT per transaction.";
      }
    } else {
      const availableBalance = baitcashBalance;
      if (value > availableBalance) {
        return `Insufficient BAITcash balance. Available: ${availableBalance.toFixed(
          2
        )} PKR`;
      }
      if (value > 100000000) {
        return "Maximum conversion amount is 100,000,000 PKR per transaction.";
      }
    }

    return "";
  };

  const handleReviewConversion = () => {
    fetchNativeBalance();
    fetchBaitcashBalance();

    setTimeout(() => {
      const validationError = validateConversion();
      if (validationError) {
        setConversionError(validationError);
        return;
      }
      setConversionError("");
      setConversionStep("confirm");
    }, 300);
  };

  const handleExecuteConversion = async () => {
    setConversionLoading(true);
    setConversionError("");

    try {
      await fetchNativeBalance();
      await fetchBaitcashBalance();

      const value = parseFloat(conversionAmount);
      const currentBaitBalance = baitTokenBalance;
      const currentPKRBalance = baitcashBalance;

      if (!baitcashAccount) {
        setConversionError("Please activate BAITcash first.");
        setConversionLoading(false);
        return;
      }

      if (conversionMode === "baitToPkr") {
        if (value > currentBaitBalance) {
          setConversionError(
            `Insufficient BAIT balance. Available: ${currentBaitBalance.toFixed(
              4
            )} BAIT`
          );
          setConversionLoading(false);
          return;
        }
      } else {
        if (value > currentPKRBalance) {
          setConversionError(
            `Insufficient BAITcash balance. Available: ${currentPKRBalance.toFixed(
              2
            )} PKR`
          );
          setConversionLoading(false);
          return;
        }
      }

      let endpoint = "";
      let payload = {};

      if (conversionMode === "baitToPkr") {
        if (!seedPhrase) {
          throw new Error(
            "Seed phrase is missing. Please restart your wallet."
          );
        }

        let cleanSeed = seedPhrase.trim();
        if (cleanSeed.startsWith("0x")) {
          cleanSeed = cleanSeed.substring(2);
        }

        const provider = new ethers.JsonRpcProvider(
          "https://rpc.goldchain.tech"
        );
        const walletSigner = ethers.Wallet.fromPhrase(cleanSeed, provider);

        const burnAddress =
          "0x0000000000000000000000000000000000000000";
        const amountWei = ethers.parseEther(value.toString());

        const txRequest = {
          to: burnAddress,
          value: amountWei,
        };
        const populatedTx = await walletSigner.populateTransaction(txRequest);
        const signedTx = await walletSigner.signTransaction(populatedTx);

        endpoint = `${USERS_API}/convert/bait-to-pkr`;
        payload = {
          walletAddress: walletSigner.address,
          amountBAIT: value,
          phoneNumber: baitcashAccount?.phoneNumber || "",
          signedTx: signedTx,
        };
      } else {
        endpoint = `${USERS_API}/convert/pkr-to-bait`;
        payload = {
          walletAddress: wallet,
          amountPKR: value,
          phoneNumber: baitcashAccount?.phoneNumber || "",
        };
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || !data?.success) {
        throw new Error(data?.error || "Conversion failed");
      }

      let newPKRBalance = currentPKRBalance;
      let newBaitBalance = currentBaitBalance;

      if (data.conversion?.newBalance !== undefined) {
        newPKRBalance = parseFloat(data.conversion.newBalance);
      } else if (conversionMode === "baitToPkr") {
        newPKRBalance = currentPKRBalance + value * baitPricePkr;
      } else {
        newPKRBalance = currentPKRBalance - value;
      }

      if (data.conversion?.newBaitBalance !== undefined) {
        newBaitBalance = parseFloat(data.conversion.newBaitBalance);
      } else if (conversionMode === "baitToPkr") {
        newBaitBalance = Math.max(0, currentBaitBalance - value);
      } else {
        const baitAmount = value / baitPricePkr;
        newBaitBalance = currentBaitBalance + baitAmount;
      }

      setBaitcashBalance(newPKRBalance);
      setBaitTokenBalance(newBaitBalance);
      setNativeBalance(newBaitBalance);

      setConversionResult({
        type: conversionMode,
        fromAmount: value,
        fromCurrency: conversionMode === "baitToPkr" ? "BAIT" : "PKR",
        toAmount:
          conversionMode === "baitToPkr"
            ? value * baitPricePkr
            : value / baitPricePkr,
        toCurrency: conversionMode === "baitToPkr" ? "PKR" : "BAIT",
        rate: baitPricePkr,
        newBalance: newPKRBalance,
        newBaitBalance: newBaitBalance,
        reference: data.conversion?.reference || `CONV-${Date.now()}`,
        transactionHash: data.conversion?.transactionHash,
        timestamp: new Date().toLocaleString(),
      });

      setConversionStep("success");

      await fetchBaitcashBalance();
      await fetchNativeBalance();
      await fetchTransactions();
      await fetchGoldReference(false);

      message.success("Conversion successful!");
    } catch (err) {
      console.error("Conversion failed:", err);
      let errorMsg = err?.message || "Conversion failed. Please try again.";
      if (errorMsg.includes("insufficient funds")) {
        errorMsg = "Insufficient BAIT balance on the blockchain.";
      } else if (errorMsg.includes("rejected")) {
        errorMsg = "Transaction signing was rejected.";
      } else if (errorMsg.includes("invalid mnemonic")) {
        errorMsg =
          "Invalid seed phrase. Please check your wallet recovery phrase.";
      }
      setConversionError(errorMsg);
    } finally {
      setConversionLoading(false);
    }
  };

  const resetConversionModal = () => {
    setConversionAmount("");
    setConversionError("");
    setConversionStep("form");
    setConversionResult(null);
    setConversionLoading(false);
  };

  const openConversionPage = () => {
    navigate("/convert", {
      state: {
        wallet,
        seedPhrase,
        baitcashAccount,
        baitcashBalance,
      },
    });
  };

  // ==========================================================
  // DONATE
  // ==========================================================

  const handleDonate = async () => {
    if (!donationAmount || donationAmount <= 0) {
      message.error("Please enter a valid donation amount");
      return;
    }

    message.success(
      `Thank you for donating ${donationAmount} BAIT as ${donationType}!`
    );

    setShowDonateModal(false);
    setDonationAmount("");

    navigate("/donation-success", {
      state: {
        message: `Thank you for donating ${donationAmount} BAIT as ${donationType}!`,
      },
    });
  };

  // ==========================================================
  // ZAKAT
  // ==========================================================

  const handleReceiveZakat = async () => {
    if (!recipientZakatAddress || !zakatAmount) {
      message.error("Please fill all fields");
      return;
    }

    message.success(`Zakat of ${zakatAmount} BAIT has been sent`);

    setShowZakatModal(false);
    setRecipientZakatAddress("");
    setZakatAmount("");

    navigate("/zakat-claim", {
      state: {
        zakatAmount,
        recipientZakatAddress,
        eligible: true,
      },
    });
  };

  // ==========================================================
  // OPEN ZAKAT PAGE
  // ==========================================================

  const openZakatPage = () => {
    if (!baitcashAccount) {
      message.warning("Please activate BAITcash first to claim zakat.");
      return;
    }
    navigate("/zakat", {
      state: {
        wallet,
        baitcashAccount,
      },
    });
  };

  // ==========================================================
  // OPEN DONATE PAGE
  // ==========================================================

  const openDonatePage = () => {
    if (!baitcashAccount) {
      message.warning("Please activate BAITcash first to donate.");
      return;
    }
    navigate("/donate", {
      state: {
        wallet,
        baitcashAccount,
      },
    });
  };

  // ==========================================================
  // OPEN GOVERNMENT PAYMENTS PAGE
  // ==========================================================

  const openGovtPayPage = () => {
    if (!baitcashAccount) {
      message.warning(
        "Please activate BAITcash first to make government payments."
      );
      return;
    }
    navigate("/govt-pay", {
      state: {
        wallet,
        baitcashAccount,
        baitcashBalance,
      },
    });
  };

  // ==========================================================
  // OPEN WOMEN EMPOWERMENT FUND PAGE
  // ==========================================================

  const openWomenFundPage = () => {
    if (!baitcashAccount) {
      message.warning(
        "Please activate BAITcash first to apply for funds."
      );
      return;
    }
    navigate("/women-fund", {
      state: {
        wallet,
        baitcashAccount,
        baitcashBalance,
      },
    });
  };

  const openMarketplacePage = () => {
    navigate("/marketplace");
  };

  // ==========================================================
  // GOVERNMENT PAYMENT
  // ==========================================================

  const handleGovtPayment = async () => {
    if (!paymentAmount || paymentAmount <= 0 || !govtReference) {
      message.error("Please fill all fields");
      return;
    }

    message.success(
      `${paymentType} payment of ${paymentAmount} BAIT submitted successfully!`
    );

    setShowGovtModal(false);
    setPaymentAmount("");
    setGovtReference("");
  };

  // ==========================================================
  // WOMEN FUND FILE
  // ==========================================================

  const handleFileUpload = (e) => {
    const file = e.target.files[0];

    if (file) {
      setCvFile(file);
    }
  };

  // ==========================================================
  // WOMEN FUND
  // ==========================================================

  const handleWomenFundSubmit = () => {
    if (!reasonForFunds || !profession || !amount || !cvFile) {
      message.error(
        "Please fill out all fields and upload a CV/Resume."
      );
      return;
    }

    message.success("Application submitted successfully!");

    setShowWomenModal(false);
    setReasonForFunds("");
    setProfession("");
    setAmount("");
    setCvFile(null);
  };

  // ==========================================================
  // QUICK ACTION MENU
  // ==========================================================

  const menuItems = [
    {
      id: "send",
      label: "Send PKR",
      icon: PaperAirplaneIcon,
      color: "#9a6a18",
      bgColor: "#f6eddf",
      onClick: openSendPkrPage,
    },
    {
      id: "receive",
      label: "Receive PKR",
      icon: ArrowDownTrayIcon,
      color: "#059669",
      bgColor: "#ecfdf5",
      onClick: openReceivePage,
    },
    {
      id: "convert",
      label: "Convert",
      icon: ArrowsRightLeftIcon,
      color: "#7c3aed",
      bgColor: "#ede9fe",
      onClick: openConversionPage,
    },
    {
      id: "zakat",
      label: "Zakat",
      icon: HeartIcon,
      color: "#14b8a6",
      bgColor: "#f0fdfa",
      onClick: openZakatPage,
    },
    {
      id: "donate",
      label: "Donate",
      icon: HeartIcon,
      color: "#ef4444",
      bgColor: "#fef2f2",
      onClick: openDonatePage,
    },
    {
      id: "govt",
      label: "Govt Pay",
      icon: ScaleIcon,
      color: "#fbbf24",
      bgColor: "#fefce8",
      onClick: openGovtPayPage,
    },
    {
      id: "women",
      label: "Women Fund",
      icon: UserGroupIcon,
      color: "#ec4899",
      bgColor: "#fdf2f8",
      onClick: openWomenFundPage,
    },
    {
      id: "marketplace",
      label: "Marketplace",
      icon: ShoppingBagIcon,
      color: "#8b5cf6",
      bgColor: "#ede9fe",
      onClick: openMarketplacePage,
    },
  ];

  // ==========================================================
  // NETWORK TICKER
  // ==========================================================

  const ticker =
    selectedChain === "0x89"
      ? "POL"
      : selectedChain === "0x1"
      ? "ETH"
      : "BAIT";

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div style={styles.container} ref={dashboardAppRef}>
      {/* GREETING */}
      <div style={styles.dashboardGreeting}>
        <div>
          <div style={styles.greeting}>
            {greeting}
            {baitcashAccount?.fullName
              ? `, ${baitcashAccount.fullName}`
              : ""}
            !
          </div>
          <div style={styles.time}>{currentTime}</div>
        </div>
      </div>

      {/* ---------- PREMIUM OVAL TOGGLE ---------- */}
      <div style={styles.modeToggleWrapper}>
        <Segmented
          className="app-mode-toggle premium-toggle"
          options={[
            { label: "BAITPAY", value: "BAITPAY" },
            { label: "BAITCASH", value: "BAITCASH" },
          ]}
          value={appMode}
          onChange={(value) => {
            setAppMode(value);
            if (value === "BAITCASH") {
              navigate("/dashboard");
            } else {
              navigate("/yourwallet");
            }
          }}
          size="small"
          style={{
            background:
              "linear-gradient(135deg, #c89432, #9a6a18)",
            borderRadius: "50px",
            padding: "4px",
            boxShadow: "0 8px 24px rgba(154,106,24,0.35)",
            border: "1px solid rgba(255,215,0,0.3)",
            backdropFilter: "blur(4px)",
          }}
        />
      </div>

      {/* BALANCE CARD */}
      <div style={styles.balanceRow}>
        <div style={styles.balanceInfo}>
          <div style={styles.balanceLabel}>
            {baitcashAccount ? "BAITCASH BALANCE" : "TOTAL BALANCE"}
          </div>
          <div style={styles.balanceAmount}>
            {balanceLoading ||
            pkrRateLoading ||
            baitcashBalanceLoading ? (
              <Spin size="small" />
            ) : baitcashAccount ? (
              formatPkr(baitcashBalance)
            ) : (
              formatPkr(pkrBalance)
            )}
          </div>
          {baitcashAccount ? (
            <div
              style={{
                ...styles.baitBalance,
                display: "flex",
                alignItems: "center",
                gap: "6px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  color: "#318142",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <CheckCircleIcon width={14} height={14} />
                BAITCash Active
              </span>
              {baitPricePkr && (
                <span style={styles.baitRate}>
                  • 1 BAIT = {formatPkr(baitPricePkr)}
                </span>
              )}
              {baitcashAccount.fullName && (
                <span
                  style={{
                    color: "#8c806e",
                    fontSize: "10px",
                  }}
                >
                  • {baitcashAccount.fullName}
                </span>
              )}
            </div>
          ) : (
            <div style={styles.baitBalance}>
              {formatBalance(nativeBalance)} {ticker}
              {baitPricePkr && (
                <span style={styles.baitRate}>
                  {" "}
                  • 1 BAIT = {formatPkr(baitPricePkr)}
                </span>
              )}
            </div>
          )}
          <div style={styles.balanceCaption}>
            {baitcashAccount
              ? `Your BAITcash balance: ${formatPkr(baitcashBalance)}`
              : "Connect to BAITcash to manage PKR"}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <div
            style={{
              ...styles.qrCodeContainer,
              cursor: baitcashAccount ? "pointer" : "not-allowed",
              opacity: baitcashAccount ? 1 : 0.5,
              position: "relative",
            }}
            onClick={openReceivePage}
            title={
              baitcashAccount
                ? "Click to receive PKR"
                : "Activate BAITcash first"
            }
          >
            {baitcashAccount ? (
              <QRCodeCanvas
                value={`BAITCASH:${baitcashAccount.phoneNumber}`}
                size={90}
                bgColor="#ffffff"
                fgColor="#000000"
                level="H"
                includeMargin={true}
              />
            ) : (
              <div style={styles.noWalletText}>Activate BAITcash</div>
            )}
          </div>
          {baitcashAccount && (
            <div
              style={{
                fontSize: "8px",
                color: "#8c806e",
                textAlign: "center",
                lineHeight: "1.2",
                display: "flex",
                alignItems: "center",
                gap: "3px",
              }}
            >
              <QrCodeIcon width={12} height={12} /> Receive PKR
            </div>
          )}
          {baitcashAccount && (
            <button
              onClick={openSendPkrPage}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                padding: "6px 14px",
                background:
                  "linear-gradient(135deg, #9a6a18, #c89432)",
                border: "none",
                borderRadius: "20px",
                color: "#fff",
                fontSize: "11px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 3px 10px rgba(154,106,24,0.18)",
                width: "100%",
              }}
            >
              <PaperAirplaneIcon width={14} height={14} /> Send
            </button>
          )}
        </div>
      </div>

      {/* BAITCASH ACCOUNT */}
      <div style={styles.baitcashCard}>
        <div style={styles.baitcashTop}>
          <div style={styles.baitcashBrand}>
            <div style={styles.baitcashIcon}>
              <CreditCardIcon width={23} height={23} />
            </div>
            <div>
              <div style={styles.baitcashTitle}>BAITCash</div>
              <div style={styles.baitcashSubtitle}>
                Your PKR financial account
              </div>
            </div>
          </div>
          {baitcashLoading ? (
            <Spin size="small" />
          ) : baitcashAccount?.status === "active" ? (
            <div style={styles.activatedBadge}>
              <CheckCircleIcon width={15} height={15} /> Activated
            </div>
          ) : baitcashAccount?.status === "pending" ? (
            <div style={styles.inactiveBadge}>Pending</div>
          ) : (
            <div style={styles.inactiveBadge}>Not Activated</div>
          )}
        </div>

        {baitcashAccount ? (
          <div style={styles.baitcashAccountRow}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.accountLabel}>BAITcash ACCOUNT</div>
              <div style={styles.accountNumber}>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <UserPlusIcon width={16} height={16} />
                  {baitcashAccount.fullName || "User"}
                </span>
              </div>
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "12px",
                  lineHeight: "16px",
                  color: "#8f9aaa",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <PhoneIcon width={14} height={14} />
                {baitcashAccount.phoneNumber}
              </div>
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "10px",
                  lineHeight: "14px",
                  color: "#8f9aaa",
                  wordBreak: "break-all",
                  overflowWrap: "anywhere",
                }}
              >
                {baitcashAccount.walletAddress ||
                  (wallet ? formatAddress(wallet) : "Not connected")}
              </div>
            </div>
            <div style={styles.accountStatusIcon}>
              <ShieldCheckIcon width={22} height={22} />
            </div>
          </div>
        ) : (
          <>
            {!showBaitcashInput ? (
              <>
                <div
                  style={{
                    ...styles.baitcashActivationText,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "9px",
                  }}
                >
                  <LockClosedIcon
                    width={18}
                    height={18}
                    style={{ flexShrink: 0, marginTop: "1px" }}
                  />
                  <span>
                    Activate BAITcash to manage PKR payments,
                    transfers and your BAITcash account.
                  </span>
                </div>
                <button
                  type="button"
                  style={styles.baitcashActivateButton}
                  onClick={() => {
                    setBaitcashError("");
                    setShowBaitcashInput(true);
                  }}
                >
                  <UserPlusIcon width={18} height={18} /> Activate
                  BAITcash
                </button>
              </>
            ) : (
              <div style={{ width: "100%", marginTop: "14px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    marginBottom: "6px",
                    color: "#aeb7c5",
                  }}
                >
                  Full Name
                </div>
                <div
                  style={{
                    marginBottom: "12px",
                    height: "44px",
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    padding: "0 13px",
                    borderRadius: "10px",
                    border: "1px solid rgba(255,255,255,0.13)",
                    background: "rgba(255,255,255,0.045)",
                    boxSizing: "border-box",
                  }}
                >
                  <UserPlusIcon
                    width={18}
                    height={18}
                    style={{ flexShrink: 0, color: "#c89432" }}
                  />
                  <input
                    type="text"
                    autoComplete="name"
                    value={baitcashFullName}
                    onChange={(e) => {
                      setBaitcashFullName(e.target.value);
                    }}
                    placeholder="Enter your full name"
                    style={{
                      flex: "1 1 auto",
                      minWidth: 0,
                      width: "100%",
                      height: "100%",
                      padding: 0,
                      margin: 0,
                      border: "none",
                      outline: "none",
                      background: "transparent",
                      color: "#080808",
                      fontSize: "15px",
                      fontWeight: "500",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    marginBottom: "6px",
                    color: "#aeb7c5",
                  }}
                >
                  Pakistan Mobile Number
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "stretch",
                    gap: "8px",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      flex: "1 1 auto",
                      minWidth: 0,
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                      gap: "9px",
                      padding: "0 13px",
                      borderRadius: "10px",
                      border:
                        "1px solid rgba(255,255,255,0.13)",
                      background: "rgba(255,255,255,0.045)",
                      boxSizing: "border-box",
                    }}
                  >
                    <PhoneIcon
                      width={18}
                      height={18}
                      style={{ flexShrink: 0, color: "#c89432" }}
                    />
                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      value={baitcashPhone}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /\D/g,
                          ""
                        );
                        setBaitcashPhone(value);
                      }}
                      placeholder="03001234567"
                      maxLength={11}
                      style={{
                        flex: "1 1 auto",
                        minWidth: 0,
                        width: "100%",
                        height: "100%",
                        padding: 0,
                        margin: 0,
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        color: "#080808",
                        fontSize: "15px",
                        fontWeight: "500",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={activateBaitcash}
                    disabled={baitcashLoading}
                    style={{
                      flex: "0 0 82px",
                      width: "82px",
                      height: "44px",
                      padding: "0 8px",
                      border: "none",
                      borderRadius: "10px",
                      background:
                        "linear-gradient(135deg, #9a6a18, #c89432)",
                      color: "#080808",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: baitcashLoading
                        ? "not-allowed"
                        : "pointer",
                      whiteSpace: "nowrap",
                      opacity: baitcashLoading ? 0.65 : 1,
                      transition: "all 0.2s ease",
                      boxShadow:
                        "0 3px 10px rgba(154,106,24,0.18)",
                    }}
                  >
                    {baitcashLoading ? "..." : "Register"}
                  </button>
                </div>
                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "10px",
                    color: "#0a0a0a",
                  }}
                >
                  Enter your 11-digit Pakistan mobile number.
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShowBaitcashInput(false);
                    setBaitcashPhone("");
                    setBaitcashFullName("");
                    setBaitcashError("");
                  }}
                  style={{
                    marginTop: "8px",
                    padding: 0,
                    background: "transparent",
                    border: "none",
                    color: "#8f9aaa",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
        {baitcashError && (
          <div
            style={{
              ...styles.baitcashError,
              display: "flex",
              alignItems: "center",
              gap: "7px",
            }}
          >
            <InformationCircleIcon
              width={15}
              height={15}
              style={{ flexShrink: 0 }}
            />
            <span>{baitcashError}</span>
          </div>
        )}
      </div>

      {/* QUICK ACTIONS */}
      <div style={styles.menuGrid}>
        {menuItems.map((item) => (
          <button
            key={item.id}
            style={styles.menuItem}
            onClick={item.onClick}
          >
            <div
              style={{
                ...styles.menuIcon,
                backgroundColor: item.bgColor,
                color: item.color,
              }}
            >
              <item.icon width={24} height={24} />
            </div>
            <span style={styles.menuLabel}>{item.label}</span>
          </button>
        ))}
      </div>

      {/* TRANSACTION HISTORY */}
      <div style={styles.transactionSection}>
        <div style={styles.transactionHeader}>
          <h3 style={styles.transactionTitle}>Recent Activity</h3>
          {transactionsLoading && <Spin size="small" />}
        </div>
        {transactions.length === 0 && !transactionsLoading ? (
          <p style={styles.noTransactions}>No transactions yet</p>
        ) : (
          <>
            <div style={styles.transactionList}>
              {(showAllTransactions
                ? transactions
                : transactions.slice(0, 3)
              ).map((tx) => {
                const isOutgoing =
                  tx.type === "transfer_out" || tx.type === "debit";
                const label =
                  tx.type === "credit" || tx.type === "debit"
                    ? "Conversion"
                    : isOutgoing
                    ? "Sent PKR"
                    : "Received PKR";
                const amount = Number(tx.amountPKR);
                const displayAmount = isOutgoing
                  ? `-${formatPkr(amount)}`
                  : `+${formatPkr(amount)}`;
                const counterparty =
                  tx.counterpartyPhone ||
                  tx.counterpartyWallet ||
                  "—";
                const date = new Date(tx.createdAt).toLocaleString();

                return (
                  <div key={tx._id} style={styles.transactionItem}>
                    <div
                      style={{
                        ...styles.txIcon,
                        backgroundColor: isOutgoing
                          ? "#fee2e2"
                          : "#dcfce7",
                        color: isOutgoing ? "#dc2626" : "#16a34a",
                      }}
                    >
                      {isOutgoing ? (
                        <ArrowUpTrayIcon width={18} />
                      ) : (
                        <ArrowDownTrayIcon width={18} />
                      )}
                    </div>
                    <div style={styles.txDetails}>
                      <div style={styles.txType}>{label}</div>
                      <div style={styles.txCounterparty}>
                        {counterparty}
                      </div>
                      <div style={styles.txTime}>{date}</div>
                    </div>
                    <div
                      style={{
                        ...styles.txAmount,
                        color: isOutgoing ? "#dc2626" : "#16a34a",
                      }}
                    >
                      {displayAmount}
                    </div>
                  </div>
                );
              })}
            </div>
            {transactions.length > 3 && (
              <button
                onClick={() =>
                  setShowAllTransactions(!showAllTransactions)
                }
                style={styles.viewMoreButton}
              >
                {showAllTransactions ? "View Less" : "View More"}
              </button>
            )}
          </>
        )}
      </div>

      {/* DEBIT CARD */}
      <div style={styles.cardBanner}>
        <div style={styles.cardContent}>
          <div style={styles.cardTitle}>BAIT Pay Card</div>
          <div style={styles.cardSubtitle}>
            Get your virtual debit card
          </div>
          <button
            style={styles.cardButton}
            onClick={() => message.info("Coming soon!")}
          >
            Apply Now →
          </button>
        </div>
        <img src={baitCard} alt="BAIT Card" style={styles.cardImage} />
      </div>

      {/* RECEIVE PKR MODAL */}
      <Modal
        title={null}
        open={showReceivePkrModal}
        onCancel={() => {
          setShowReceivePkrModal(false);
          resetReceiveModal();
        }}
        footer={null}
        width={400}
        centered
        closable={true}
        maskClosable={true}
        destroyOnClose={true}
        styles={{
          body: { padding: 0, borderRadius: "16px" },
          content: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
          mask: { backdropFilter: "blur(4px)" },
        }}
      >
        <div style={styles.receivePkrModal}>
          <div style={styles.receiveModalHeader}>
            <div style={styles.receiveHeaderIcon}>
              <ArrowDownTrayIcon width={22} height={22} />
            </div>
            <div>
              <div style={styles.receiveModalTitle}>Receive PKR</div>
              <div style={styles.receiveModalSubtitle}>
                Share your QR code to receive payments
              </div>
            </div>
          </div>
          {baitcashAccount ? (
            <>
              <div style={styles.receiveQrWrapper}>
                <div style={styles.receiveQrBox}>
                  <QRCodeCanvas
                    value={`BAITCASH:${baitcashAccount.phoneNumber}`}
                    size={180}
                    bgColor="#ffffff"
                    fgColor="#1a1a1a"
                    level="H"
                    includeMargin={true}
                  />
                </div>
              </div>
              <div style={styles.receiveUserInfo}>
                <div style={styles.receiveUserName}>
                  <UserIcon width={14} height={14} />{" "}
                  {baitcashAccount.fullName}
                </div>
                <div style={styles.receiveUserPhone}>
                  <PhoneIcon width={13} height={13} />{" "}
                  {baitcashAccount.phoneNumber}
                </div>
              </div>
              <button
                onClick={handleCopyReceiveInfo}
                style={styles.receiveCopyButton}
              >
                <DocumentDuplicateIcon width={16} height={16} />
                {receiveCopied ? "Copied!" : "Copy Phone Number"}
              </button>
              <div style={styles.receiveInstruction}>
                <InformationCircleIcon width={16} height={16} />
                <span>
                  Anyone with this QR code can send you PKR directly.
                  Your BAIT Cash balance will update automatically.
                </span>
              </div>
              <button
                onClick={() => {
                  setShowReceivePkrModal(false);
                  resetReceiveModal();
                }}
                style={styles.receiveDoneButton}
              >
                Done
              </button>
            </>
          ) : (
            <div style={styles.receiveNotActivated}>
              <LockClosedIcon
                width={36}
                height={36}
                style={{ color: "#9a6a18" }}
              />
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#333",
                  margin: "8px 0 4px",
                }}
              >
                BAITcash Not Activated
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#888",
                  margin: "0 0 12px",
                }}
              >
                Activate your BAITcash account first to receive PKR.
              </p>
              <button
                onClick={() => {
                  setShowReceivePkrModal(false);
                  setShowBaitcashInput(true);
                }}
                style={styles.receiveActivateButton}
              >
                <UserPlusIcon width={18} height={18} /> Activate Now
              </button>
            </div>
          )}
        </div>
      </Modal>

      {/* CONVERSION MODAL */}
      <Modal
        title={null}
        open={showConversionModal}
        onCancel={() => {
          if (!conversionLoading) {
            setShowConversionModal(false);
            resetConversionModal();
          }
        }}
        footer={null}
        width={460}
        centered
        closable={!conversionLoading}
        maskClosable={!conversionLoading}
        destroyOnClose={true}
        styles={{
          body: { padding: 0, borderRadius: "16px" },
          content: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
          mask: { backdropFilter: "blur(4px)" },
        }}
      >
        <div style={styles.pkrModal}>
          {conversionStep === "success" && conversionResult ? (
            // Success Screen
            <>
              <div style={styles.sendSuccessHeader}>
                <CheckCircleIcon
                  width={48}
                  height={48}
                  style={{ color: "#4cae5b" }}
                />
                <h2
                  style={{
                    margin: "12px 0 4px",
                    fontSize: "20px",
                    fontWeight: 700,
                  }}
                >
                  Conversion Successful
                </h2>
                <p style={{ fontSize: "14px", color: "#666" }}>
                  Your{" "}
                  {conversionMode === "baitToPkr" ? "BAIT" : "PKR"} has
                  been converted successfully
                </p>
              </div>
              <div style={styles.sendSuccessAmount}>
                {conversionMode === "baitToPkr"
                  ? formatPkr(conversionResult.toAmount)
                  : `${conversionResult.toAmount.toFixed(6)} BAIT`}
              </div>
              <div style={styles.sendSuccessCard}>
                <div style={styles.sendSuccessRow}>
                  <span>Amount Sent</span>
                  <strong>
                    {conversionMode === "baitToPkr"
                      ? `${conversionResult.fromAmount.toFixed(4)} BAIT`
                      : `${formatPkr(conversionResult.fromAmount)}`}
                  </strong>
                </div>
                <div style={styles.sendSuccessRow}>
                  <span>Amount Received</span>
                  <strong>
                    {conversionMode === "baitToPkr"
                      ? formatPkr(conversionResult.toAmount)
                      : `${conversionResult.toAmount.toFixed(6)} BAIT`}
                  </strong>
                </div>
                <div style={styles.sendSuccessRow}>
                  <span>Rate</span>
                  <strong>
                    1 BAIT = {formatPkr(conversionResult.rate)}
                  </strong>
                </div>
                {conversionResult.transactionHash && (
                  <div style={styles.sendSuccessRow}>
                    <span>Transaction</span>
                    <strong
                      style={{
                        fontSize: "10px",
                        wordBreak: "break-all",
                      }}
                    >
                      {conversionResult.transactionHash}
                    </strong>
                  </div>
                )}
                <div style={styles.sendSuccessRow}>
                  <span>Reference</span>
                  <strong
                    style={{
                      fontSize: "11px",
                      wordBreak: "break-all",
                    }}
                  >
                    {conversionResult.reference}
                  </strong>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowConversionModal(false);
                  resetConversionModal();
                }}
                style={styles.sendDoneButton}
              >
                Done
              </button>
            </>
          ) : conversionStep === "confirm" ? (
            // Confirm Screen
            <>
              <div style={styles.pkrModalHeader}>
                <div style={styles.pkrHeaderIcon}>
                  <ArrowsRightLeftIcon width={25} height={25} />
                </div>
                <div>
                  <div style={styles.pkrModalTitle}>
                    Confirm Conversion
                  </div>
                  <div style={styles.pkrModalSubtitle}>
                    Review the details before confirming
                  </div>
                </div>
              </div>
              <div style={{ padding: "0 20px 16px" }}>
                {conversionError && (
                  <div style={styles.sendErrorBox}>
                    <InformationCircleIcon width={18} height={18} />
                    <span>{conversionError}</span>
                  </div>
                )}
                <div style={styles.sendConfirmCard}>
                  <div style={styles.sendConfirmIcon}>
                    {conversionMode === "baitToPkr" ? (
                      <ArrowUpTrayIcon width={30} height={30} />
                    ) : (
                      <ArrowDownTrayIcon width={30} height={30} />
                    )}
                  </div>
                  <p style={styles.sendConfirmLabel}>
                    You are converting
                  </p>
                  <div
                    style={{
                      fontSize: "24px",
                      fontWeight: 800,
                      color: "#1a1a1a",
                      margin: "4px 0",
                    }}
                  >
                    {conversionMode === "baitToPkr"
                      ? `${parseFloat(conversionAmount).toFixed(
                          4
                        )} BAIT → ${formatPkr(convertedAmount)}`
                      : `${formatPkr(
                          parseFloat(conversionAmount)
                        )} → ${convertedAmount.toFixed(6)} BAIT`}
                  </div>
                  <div style={styles.sendConfirmPhone}>
                    Rate: 1 BAIT = {formatPkr(baitPricePkr)}
                  </div>
                </div>
                <div style={styles.sendButtonRow}>
                  <button
                    onClick={() => setConversionStep("form")}
                    disabled={conversionLoading}
                    style={styles.sendCancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleExecuteConversion}
                    disabled={conversionLoading}
                    style={styles.sendConfirmButton}
                  >
                    {conversionLoading ? (
                      <>
                        <Spin
                          size="small"
                          style={{ marginRight: 8 }}
                        />{" "}
                        Processing...
                      </>
                    ) : (
                      <>
                        {" "}
                        Confirm <ArrowRightIcon
                          width={18}
                          height={18}
                        />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : (
            // Form Screen
            <>
              <div style={styles.pkrModalHeader}>
                <div style={styles.pkrHeaderIcon}>
                  <ArrowsRightLeftIcon width={25} height={25} />
                </div>
                <div>
                  <div style={styles.pkrModalTitle}>
                    Convert BAIT
                  </div>
                  <div style={styles.pkrModalSubtitle}>
                    Convert between BAIT and PKR
                  </div>
                </div>
              </div>
              <div style={{ padding: "0 20px 16px" }}>
                {conversionError && (
                  <div style={styles.sendErrorBox}>
                    <InformationCircleIcon width={18} height={18} />
                    <span>{conversionError}</span>
                  </div>
                )}
                <div style={styles.conversionToggle}>
                  <button
                    style={{
                      ...styles.toggleButton,
                      backgroundColor:
                        conversionMode === "baitToPkr"
                          ? "#9a6a18"
                          : "#f0f0f0",
                      color:
                        conversionMode === "baitToPkr"
                          ? "#fff"
                          : "#666",
                    }}
                    onClick={() => {
                      setConversionMode("baitToPkr");
                      setConversionAmount("");
                      setConversionError("");
                    }}
                  >
                    BAIT → PKR
                  </button>
                  <button
                    style={{
                      ...styles.toggleButton,
                      backgroundColor:
                        conversionMode === "pkrToBait"
                          ? "#9a6a18"
                          : "#f0f0f0",
                      color:
                        conversionMode === "pkrToBait"
                          ? "#fff"
                          : "#666",
                    }}
                    onClick={() => {
                      setConversionMode("pkrToBait");
                      setConversionAmount("");
                      setConversionError("");
                    }}
                  >
                    PKR → BAIT
                  </button>
                </div>
                <div style={styles.rateCard}>
                  <div style={styles.rateCardLeft}>
                    <div style={styles.rateIconCircle}>
                      <img
                        src={BaitLogo}
                        alt="BAIT"
                        style={styles.rateLogo}
                      />
                    </div>
                    <div>
                      <div style={styles.rateLabel}>
                        LIVE BAIT RATE
                      </div>
                      <div style={styles.rateValue}>
                        {pkrRateLoading ? (
                          <Spin size="small" />
                        ) : baitPricePkr ? (
                          `₨ ${baitPricePkr.toLocaleString("en-PK", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}`
                        ) : (
                          "Loading..."
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={styles.liveBadge}>
                    <span style={styles.liveDot} /> LIVE
                  </div>
                </div>
                <div style={styles.conversionBalanceRow}>
                  <div style={styles.conversionBalanceItem}>
                    <span style={styles.balanceLabel}>
                      BAIT Balance
                    </span>
                    <span style={styles.balanceValue}>
                      {baitTokenBalanceLoading ? (
                        <Spin size="small" />
                      ) : (
                        `${formatBalance(baitTokenBalance)} BAIT`
                      )}
                    </span>
                  </div>
                  <div style={styles.conversionBalanceDivider} />
                  <div style={styles.conversionBalanceItem}>
                    <span style={styles.balanceLabel}>
                      BAITcash Balance
                    </span>
                    <span style={styles.balanceValue}>
                      {formatPkr(baitcashBalance)}
                    </span>
                  </div>
                </div>
                <div
                  style={
                    conversionMode === "baitToPkr"
                      ? styles.amountCardBait
                      : styles.amountCard
                  }
                >
                  <div style={styles.amountHeader}>
                    <div style={styles.amountLabel}>
                      YOU CONVERT
                    </div>
                    <div
                      style={
                        conversionMode === "baitToPkr"
                          ? styles.currencyBadgeBait
                          : styles.currencyBadge
                      }
                    >
                      {conversionMode === "baitToPkr"
                        ? "BAIT"
                        : "PKR ₨"}
                    </div>
                  </div>
                  <div style={styles.amountInputWrapper}>
                    {conversionMode === "baitToPkr" ? (
                      <img
                        src={BaitLogo}
                        alt="BAIT"
                        style={styles.amountLogo}
                      />
                    ) : (
                      <span style={styles.currencySymbol}>₨</span>
                    )}
                    <Input
                      bordered={false}
                      type="number"
                      value={conversionAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (
                          value === "" ||
                          /^\d*(\.\d{0,4})?$/.test(value)
                        ) {
                          setConversionAmount(value);
                          setConversionError("");
                        }
                      }}
                      placeholder={
                        conversionMode === "baitToPkr"
                          ? "0.0000"
                          : "0.00"
                      }
                      style={styles.amountInput}
                    />
                  </div>
                  <div style={styles.amountHint}>
                    Available:{" "}
                    {conversionMode === "baitToPkr"
                      ? `${formatBalance(baitTokenBalance)} BAIT`
                      : `${formatPkr(baitcashBalance)}`}
                  </div>
                </div>
                {conversionAmount &&
                  parseFloat(conversionAmount) > 0 &&
                  baitPricePkr && (
                    <>
                      <div style={styles.conversionArrow}>
                        <div style={styles.arrowCircle}>
                          <ArrowDownTrayIcon
                            width={19}
                            height={19}
                          />
                        </div>
                      </div>
                      <div
                        style={
                          conversionMode === "baitToPkr"
                            ? styles.amountCard
                            : styles.amountCardBait
                        }
                      >
                        <div style={styles.amountHeader}>
                          <div style={styles.amountLabel}>
                            YOU RECEIVE
                          </div>
                          <div
                            style={
                              conversionMode === "baitToPkr"
                                ? styles.currencyBadge
                                : styles.currencyBadgeBait
                            }
                          >
                            {conversionMode === "baitToPkr"
                              ? "PKR ₨"
                              : "BAIT"}
                          </div>
                        </div>
                        <div style={styles.amountInputWrapper}>
                          {conversionMode === "baitToPkr" ? (
                            <span style={styles.currencySymbol}>
                              ₨
                            </span>
                          ) : (
                            <img
                              src={BaitLogo}
                              alt="BAIT"
                              style={styles.amountLogo}
                            />
                          )}
                          <Input
                            bordered={false}
                            value={
                              conversionMode === "baitToPkr"
                                ? convertedAmount.toFixed(2)
                                : convertedAmount.toFixed(6)
                            }
                            style={styles.amountInput}
                            disabled
                          />
                        </div>
                        <div style={styles.amountHint}>
                          Approximate amount at current BAIT/PKR
                          rate
                        </div>
                      </div>
                    </>
                  )}
                <button
                  style={{
                    ...styles.pkrTransferButton,
                    opacity:
                      conversionLoading ||
                      !conversionAmount ||
                      parseFloat(conversionAmount) <= 0
                        ? 0.55
                        : 1,
                    cursor:
                      conversionLoading ||
                      !conversionAmount ||
                      parseFloat(conversionAmount) <= 0
                        ? "not-allowed"
                        : "pointer",
                  }}
                  onClick={handleReviewConversion}
                  disabled={
                    conversionLoading ||
                    !conversionAmount ||
                    parseFloat(conversionAmount) <= 0
                  }
                >
                  {conversionLoading ? (
                    <>
                      <Spin
                        size="small"
                        style={{ marginRight: 8 }}
                      />{" "}
                      Processing...
                    </>
                  ) : (
                    <>
                      {" "}
                      Review Conversion{" "}
                      <ArrowRightIcon width={19} height={19} />
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* OTHER MODALS (Donate, Zakat, Govt, Women) */}
      <Modal
        title="Donate to Charity"
        open={showDonateModal}
        onCancel={() => setShowDonateModal(false)}
        footer={null}
        width={400}
        centered
        destroyOnClose={true}
        styles={{
          body: { padding: "20px" },
          content: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
          mask: { backdropFilter: "blur(4px)" },
        }}
      >
        <div style={styles.modalContent}>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Donation Type</label>
            <select
              value={donationType}
              onChange={(e) => setDonationType(e.target.value)}
              style={styles.modalSelect}
            >
              <option value="zakat">Zakat</option>
              <option value="sadaqah">Sadaqah</option>
              <option value="waqf">Waqf</option>
            </select>
          </div>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Amount (BAIT)</label>
            <Input
              type="number"
              value={donationAmount}
              onChange={(e) => setDonationAmount(e.target.value)}
              placeholder="Enter amount"
            />
          </div>
          <button onClick={handleDonate} style={styles.modalButton}>
            Donate Now
          </button>
        </div>
      </Modal>

      <Modal
        title="Receive Zakat"
        open={showZakatModal}
        onCancel={() => setShowZakatModal(false)}
        footer={null}
        width={400}
        centered
        destroyOnClose={true}
        styles={{
          body: { padding: "20px" },
          content: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
          mask: { backdropFilter: "blur(4px)" },
        }}
      >
        <div style={styles.modalContent}>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>
              Zakat Amount (BAIT)
            </label>
            <Input
              type="number"
              value={zakatAmount}
              onChange={(e) => setZakatAmount(e.target.value)}
              placeholder="Enter zakat amount"
            />
          </div>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>
              Recipient Wallet Address
            </label>
            <Input
              value={recipientZakatAddress}
              onChange={(e) =>
                setRecipientZakatAddress(e.target.value)
              }
              placeholder="Enter recipient address"
            />
          </div>
          <button
            onClick={handleReceiveZakat}
            style={styles.modalButton}
          >
            Receive Zakat
          </button>
        </div>
      </Modal>

      <Modal
        title="Government Payments"
        open={showGovtModal}
        onCancel={() => setShowGovtModal(false)}
        footer={null}
        width={400}
        centered
        destroyOnClose={true}
        styles={{
          body: { padding: "20px" },
          content: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
          mask: { backdropFilter: "blur(4px)" },
        }}
      >
        <div style={styles.modalContent}>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Payment Type</label>
            <select
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
              style={styles.modalSelect}
            >
              <option value="tax">Tax Payment</option>
              <option value="utility">Utility Bill</option>
              <option value="license">License Fee</option>
              <option value="fine">Fine Payment</option>
            </select>
          </div>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Amount (BAIT)</label>
            <Input
              type="number"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              placeholder="Enter amount"
            />
          </div>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>
              Reference Number
            </label>
            <Input
              value={govtReference}
              onChange={(e) => setGovtReference(e.target.value)}
              placeholder="Enter reference number"
            />
          </div>
          <button
            onClick={handleGovtPayment}
            style={styles.modalButton}
          >
            Pay Now
          </button>
        </div>
      </Modal>

      <Modal
        title="Women Empowerment Fund"
        open={showWomenModal}
        onCancel={() => setShowWomenModal(false)}
        footer={null}
        width={400}
        centered
        destroyOnClose={true}
        styles={{
          body: { padding: "20px" },
          content: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
          mask: { backdropFilter: "blur(4px)" },
        }}
      >
        <div style={styles.modalContent}>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>
              Reason for Funds
            </label>
            <textarea
              value={reasonForFunds}
              onChange={(e) => setReasonForFunds(e.target.value)}
              placeholder="Please provide a detailed reason"
              rows={3}
              style={styles.modalTextarea}
            />
          </div>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Profession</label>
            <Input
              value={profession}
              onChange={(e) => setProfession(e.target.value)}
              placeholder="Enter your profession"
            />
          </div>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>Amount (BAIT)</label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter amount"
            />
          </div>
          <div style={styles.modalField}>
            <label style={styles.modalLabel}>
              Upload CV/Resume
            </label>
            <input
              type="file"
              accept=".pdf, .doc, .docx"
              onChange={handleFileUpload}
              style={styles.modalFileInput}
            />
          </div>
          <button
            onClick={handleWomenFundSubmit}
            style={styles.modalButton}
          >
            Submit Application
          </button>
        </div>
      </Modal>
    </div>
  );
};

// ============================================================
// STYLES
// ============================================================

const styles = {
  // ==========================================================
  // MAIN
  // ==========================================================

  container: {
    minHeight: "100dvh",
    background: "linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)",
    padding: "0 0 30px 0",
    boxSizing: "border-box",
},

  // ==========================================================
  // GREETING
  // ==========================================================

  dashboardGreeting: {
    padding: "0 20px",
    marginTop: "10px",
  },

  greeting: {
    fontSize: 14,
    color: "rgba(27,26,26,0.8)",
    fontWeight: 700,
  },

  time: {
    fontSize: 14,
    color: "rgba(27,26,26,0.8)",
  },

  // ==========================================================
  // BALANCE
  // ==========================================================

  balanceRow: {
    width: "calc(100% - 24px)",
    maxWidth: "600px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "18px 20px",
    margin: "20px auto 14px",
    background: "#f7f5f2",
    backdropFilter: "blur(10px)",
    borderRadius: 18,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    border: "2px solid #9a6a18",
    boxSizing: "border-box",
  },

  balanceInfo: {
    flex: 1,
    minWidth: 0,
  },

  balanceLabel: {
    fontSize: 12,
    color: "rgba(22,18,18,0.7)",
    fontWeight: 900,
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: "1px",
  },

  balanceAmount: {
    fontSize: "clamp(22px, 7vw, 32px)",
    fontWeight: 800,
    color: "#212220",
     wordBreak: "break-word",
    lineHeight: 1.15,
  },

  baitBalance: {
    marginTop: 7,
    fontSize: 12,
    color: "#6f5731",
    fontWeight: 700,
  },

  baitRate: {
    color: "#8d7a5b",
    fontWeight: 500,
  },

  balanceCaption: {
    marginTop: 3,
    fontSize: 10,
    color: "#999",
  },

  qrCodeContainer: {
    width: 90,
    height: 90,
    background: "rgba(255,255,255,0.15)",
    backdropFilter: "blur(10px)",
    borderRadius: 18,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    padding: 4,
    position: "relative",
  },

  noWalletText: {
    fontSize: 11,
    color: "#302e2e",
    textAlign: "center",
  },

  // ==========================================================
  // BAITCASH
  // ==========================================================

  baitcashCard: {
    width: "calc(100% - 24px)",
    maxWidth: "600px",
    margin: "0 auto 18px",
    padding: "16px 17px",
    background: "linear-gradient(135deg, #fffdf9 0%, #f6eddf 100%)",
    border: "1px solid rgba(154,106,24,0.24)",
    borderRadius: 18,
    boxShadow: "0 4px 14px rgba(80,50,10,0.06)",
    boxSizing: "border-box",
  },

  baitcashTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },

  baitcashBrand: {
    display: "flex",
    alignItems: "center",
    gap: 11,
    minWidth: 0,
  },

  baitcashIcon: {
    width: 42,
    height: 42,
    borderRadius: 13,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg,#9a6a18,#c4933b)",
    color: "#fff",
    boxShadow: "0 4px 10px rgba(154,106,24,0.18)",
    flexShrink: 0,
  },

  baitcashTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: "#28231c",
  },

  baitcashSubtitle: {
    fontSize: 10,
    color: "#8c806e",
    marginTop: 2,
  },

  activatedBadge: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "6px 9px",
    borderRadius: 20,
    background: "#eaf7ec",
    color: "#318142",
    fontSize: 10,
    fontWeight: 800,
    flexShrink: 0,
  },

  inactiveBadge: {
    padding: "6px 9px",
    borderRadius: 20,
    background: "#f1ede7",
    color: "#87704f",
    fontSize: 10,
    fontWeight: 800,
    flexShrink: 0,
  },

  baitcashAccountRow: {
    marginTop: 14,
    padding: "11px 12px",
    borderRadius: 12,
    background: "rgba(255,255,255,0.65)",
    border: "1px solid rgba(154,106,24,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  accountLabel: {
    fontSize: 8,
    letterSpacing: "1px",
    fontWeight: 800,
    color: "#9a825f",
  },

  accountNumber: {
    marginTop: 3,
    fontSize: 13,
    fontWeight: 800,
    color: "#3d3529",
  },

  accountStatusIcon: {
    width: 35,
    height: 35,
    borderRadius: 11,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#eef8ef",
    color: "#3d914c",
  },

  baitcashOpenButton: {
    width: "100%",
    marginTop: 10,
    height: 42,
    border: "none",
    borderRadius: 12,
    background: "linear-gradient(135deg,#8d5f16,#b8862d)",
    color: "#fff",
    fontSize: 12,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    cursor: "pointer",
    boxShadow: "0 5px 13px rgba(154,106,24,0.17)",
  },

  baitcashActivationText: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginTop: 13,
    color: "#766a5a",
    fontSize: 10,
    lineHeight: 1.45,
  },

  baitcashActivateButton: {
    width: "100%",
    marginTop: 11,
    height: 42,
    border: "1px solid #9a6a18",
    borderRadius: 12,
    background: "#fff",
    color: "#8b6019",
    fontSize: 12,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    cursor: "pointer",
  },

  baitcashError: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: 9,
    color: "#9a5b12",
    fontSize: 9,
  },

  // ==========================================================
  // WALLET ADDRESS
  // ==========================================================

  walletSection: {
    padding: "0 10px",
    marginBottom: 20,
  },

  walletAddressBox: {
    background: "#f7f5f2",
    backdropFilter: "blur(10px)",
    borderRadius: 16,
    padding: "14px 18px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    border: "2px solid #9a6a18",
  },

  walletLabel: {
    fontSize: 12,
    color: "rgba(17,16,16,0.7)",
    fontWeight: 500,
  },

  walletAddress: {
    fontSize: 14,
    fontFamily: "monospace",
    color: "#181717",
    fontWeight: 600,
    flex: 1,
    marginLeft: 8,
  },

  copyIcon: {
    cursor: "pointer",
    opacity: 0.8,
    transition: "all 0.2s ease",
    flexShrink: 0,
  },

  // ==========================================================
  // MENU
  // ==========================================================

  menuGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    padding: "0 16px 20px",
},

  menuItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },

  menuIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  menuLabel: {
    fontSize: 11,
    color: "#161515",
    fontWeight: 500,
  },

  // ==========================================================
  // TRANSACTION HISTORY
  // ==========================================================

  transactionSection: {
    width: "calc(100% - 24px)",
    maxWidth: "600px",
    margin: "20px auto 16px",
    padding: "16px 18px 12px",
    background: "#ffffff",
    borderRadius: 18,
    boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
    border: "1px solid #eaeaea",
    boxSizing: "border-box",
  },

  transactionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },

  transactionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
  },

  transactionList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },

  transactionItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 0",
    borderBottom: "1px solid #f0f0f0",
  },

  txIcon: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  txDetails: {
    flex: 1,
    minWidth: 0,
  },

  txType: {
    fontSize: 13,
    fontWeight: 600,
    color: "#1a1a1a",
  },

  txCounterparty: {
    fontSize: 11,
    color: "#888",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 140,
  },

  txTime: {
    fontSize: 10,
    color: "#aaa",
    marginTop: 2,
  },

  txAmount: {
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },

  noTransactions: {
    textAlign: "center",
    color: "#aaa",
    fontSize: 14,
    padding: "20px 0",
    margin: 0,
  },

  viewMoreButton: {
    display: "block",
    width: "100%",
    marginTop: "12px",
    padding: "8px 0",
    border: "none",
    background: "transparent",
    color: "#9a6a18",
    fontWeight: 600,
    fontSize: "13px",
    cursor: "pointer",
    textAlign: "center",
    borderTop: "1px solid #f0f0f0",
    transition: "all 0.2s ease",
  },

  modeToggleWrapper: {
    display: "flex",
    justifyContent: "center",
    margin: "8px 0 18px 0",
    padding: "0 20px",
  },

  // ==========================================================
  // CARD
  // ==========================================================

  cardBanner: {
    width: "calc(100% - 24px)",
    maxWidth: "600px",
    background: "#f7f5f2",
    borderRadius: 20,
    margin: "20px auto",
    padding: "16px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    border: "2px solid #9a6a18",
    boxSizing: "border-box",
  },

  cardContent: {
    color: "#0e0d0d",
  },

  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 4,
  },

  cardSubtitle: {
    fontSize: 12,
    opacity: 0.8,
    marginBottom: 12,
  },

  cardButton: {
    background: "#e9dac4",
    border: "none",
    borderRadius: 20,
    padding: "6px 16px",
    fontSize: 12,
    fontWeight: 500,
    color: "#9a6a18",
    cursor: "pointer",
  },

  cardImage: {
    width: 120,
    height: 80,
    objectFit: "contain",
  },

  // ==========================================================
  // RECEIVE PKR MODAL
  // ==========================================================

  receivePkrModal: {
    padding: "4px 6px 10px",
    color: "#181818",
  },

  receiveModalHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "4px 4px 16px",
    borderBottom: "1px solid #f0f0f0",
  },

  receiveHeaderIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "14px",
    background: "linear-gradient(135deg, #059669, #10b981)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(5,150,105,0.2)",
    flexShrink: 0,
  },

  receiveModalTitle: {
    fontSize: "18px",
    fontWeight: 800,
    color: "#171717",
    margin: 0,
    lineHeight: 1.2,
  },

  receiveModalSubtitle: {
    fontSize: "11px",
    color: "#888",
    marginTop: "1px",
  },

  receiveQrWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: "12px 0",
  },

  receiveQrBox: {
    padding: "12px",
    background: "#ffffff",
    borderRadius: "18px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
    border: "2px solid #f0f0f0",
  },

  receiveQrPlaceholder: {
    width: "180px",
    height: "180px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    background: "#fafafa",
    borderRadius: "12px",
    color: "#999",
  },

  receiveUserInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "3px",
    marginTop: "4px",
    padding: "10px",
    background: "#f8fafc",
    borderRadius: "12px",
  },

  receiveUserName: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "14px",
    fontWeight: 700,
    color: "#1a1a1a",
  },

  receiveUserPhone: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    fontSize: "12px",
    color: "#666",
  },

  receiveCopyButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    width: "100%",
    padding: "9px",
    marginTop: "8px",
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    background: "#fff",
    color: "#333",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  receiveInstruction: {
    display: "flex",
    alignItems: "flex-start",
    gap: "7px",
    marginTop: "10px",
    padding: "8px 10px",
    background: "#f0fdf4",
    borderRadius: "10px",
    border: "1px solid #d1fae5",
    fontSize: "10px",
    color: "#065f46",
    lineHeight: 1.4,
  },

  receiveDoneButton: {
    width: "100%",
    marginTop: "12px",
    padding: "10px",
    borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg, #059669, #10b981)",
    color: "#fff",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(5,150,105,0.2)",
  },

  receiveNotActivated: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
    padding: "24px 16px",
    textAlign: "center",
    color: "#666",
  },

  receiveActivateButton: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "9px 20px",
    borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg, #9a6a18, #c89432)",
    color: "#fff",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(154,106,24,0.2)",
  },

  // ==========================================================
  // PKR MODAL
  // ==========================================================

  pkrModal: {
    padding: "4px 2px 8px",
    color: "#181818",
  },

  pkrModalHeader: {
    display: "flex",
    alignItems: "center",
    gap: 13,
    padding: "4px 2px 18px",
  },

  pkrHeaderIcon: {
    width: 46,
    height: 46,
    borderRadius: 15,
    background: "linear-gradient(135deg,#9a6a18,#c4933b)",
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 5px 14px rgba(154,106,24,0.22)",
  },

  pkrModalTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: "#171717",
    lineHeight: 1.1,
  },

  pkrModalSubtitle: {
    fontSize: 12,
    color: "#777",
    marginTop: 4,
  },

  rateCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(135deg,#f9f5ed,#f4ead9)",
    border: "1px solid rgba(154,106,24,0.25)",
    borderRadius: 16,
    padding: "12px 14px",
    marginBottom: 17,
  },

  rateCardLeft: {
    display: "flex",
    alignItems: "center",
    gap: 11,
  },

  rateIconCircle: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    background: "#ffffff",
    border: "1px solid rgba(154,106,24,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  rateLogo: {
    width: 25,
    height: 25,
    objectFit: "contain",
  },

  rateLabel: {
    fontSize: 9,
    fontWeight: 800,
    letterSpacing: "1px",
    color: "#8a6a38",
    marginBottom: 2,
  },

  rateValue: {
    fontSize: 16,
    fontWeight: 800,
    color: "#28231c",
  },

  liveBadge: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    padding: "5px 8px",
    borderRadius: 20,
    background: "#edf9ef",
    color: "#287a35",
    fontSize: 9,
    fontWeight: 800,
  },

  liveDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: "#35a848",
    display: "inline-block",
  },

  rateError: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    background: "#fff7ed",
    color: "#9a5b12",
    border: "1px solid #f0d2a4",
    borderRadius: 10,
    padding: "8px 10px",
    marginBottom: 15,
    fontSize: 11,
  },

  retryButton: {
    marginLeft: "auto",
    border: "none",
    background: "transparent",
    color: "#9a6a18",
    fontWeight: 800,
    cursor: "pointer",
  },

  transferSection: {
    marginBottom: 16,
  },

  sectionLabel: {
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "1px",
    color: "#8a8a8a",
    marginBottom: 7,
  },

  walletMiniCard: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 13,
    background: "#fafafa",
    border: "1px solid #e5e5e5",
  },

  walletMiniIcon: {
    width: 36,
    height: 36,
    borderRadius: 11,
    background: "#f4ead9",
    color: "#9a6a18",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  walletMiniInfo: {
    flex: 1,
    minWidth: 0,
  },

  walletMiniTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: "#292929",
  },

  walletMiniAddress: {
    fontSize: 10,
    color: "#858585",
    fontFamily: "monospace",
    marginTop: 2,
  },

  walletCheck: {
    color: "#4cae5b",
  },

  pkrInput: {
    borderRadius: 13,
    height: 46,
  },

  validAddress: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    marginTop: 6,
    fontSize: 10,
    color: "#3f914d",
  },

  amountCard: {
    background: "#ffffff",
    border: "1px solid #e4e4e4",
    borderRadius: 17,
    padding: "13px 14px",
    boxShadow: "0 3px 12px rgba(0,0,0,0.035)",
  },

  amountCardBait: {
    background: "linear-gradient(135deg,#fffdf8,#f9f3e8)",
    border: "1px solid rgba(154,106,24,0.3)",
    borderRadius: 17,
    padding: "13px 14px",
    boxShadow: "0 3px 12px rgba(154,106,24,0.06)",
  },

  amountHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },

  amountLabel: {
    fontSize: 9,
    fontWeight: 800,
    color: "#858585",
    letterSpacing: "0.8px",
  },

  currencyBadge: {
    background: "#f2f2f2",
    borderRadius: 8,
    padding: "4px 7px",
    fontSize: 9,
    fontWeight: 800,
    color: "#505050",
  },

  currencyBadgeBait: {
    background: "#ead9b9",
    borderRadius: 8,
    padding: "4px 8px",
    fontSize: 9,
    fontWeight: 900,
    color: "#8b6019",
  },

  amountInputWrapper: {
    display: "flex",
    alignItems: "center",
    height: 48,
  },

  currencySymbol: {
    fontSize: 23,
    fontWeight: 700,
    color: "#383838",
    marginLeft: 2,
  },

  amountLogo: {
    width: 25,
    height: 25,
    objectFit: "contain",
    marginLeft: 2,
  },

  amountInput: {
    flex: 1,
    fontSize: 25,
    fontWeight: 800,
    color: "#1f1f1f",
    paddingLeft: 8,
    paddingRight: 0,
  },

  amountHint: {
    fontSize: 9,
    lineHeight: 1.4,
    color: "#898989",
    marginTop: 2,
  },

  conversionArrow: {
    height: 18,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    zIndex: 2,
  },

  arrowCircle: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "#ffffff",
    border: "1px solid #dedede",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9a6a18",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
  },

  conversionSummary: {
    background: "#fafafa",
    borderRadius: 13,
    padding: "10px 12px",
    marginTop: 13,
    border: "1px solid #ededed",
  },

  summaryRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 10,
    color: "#777",
    padding: "4px 0",
  },

  backingInfo: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    background: "rgba(154,106,24,0.055)",
    border: "1px solid rgba(154,106,24,0.15)",
    borderRadius: 12,
    padding: "9px 10px",
    marginTop: 12,
    color: "#9a6a18",
  },

  pkrTransferButton: {
    width: "100%",
    height: 48,
    marginTop: 17,
    border: "none",
    borderRadius: 14,
    background: "linear-gradient(135deg,#8d5f16,#b8862d)",
    color: "#ffffff",
    fontSize: 14,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    boxShadow: "0 7px 18px rgba(154,106,24,0.22)",
    transition: "all 0.2s ease",
  },

  securityNote: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    marginTop: 10,
    color: "#888",
    fontSize: 9,
    textAlign: "center",
  },

  // ==========================================================
  // EXISTING MODALS
  // ==========================================================

  modalContent: {
    padding: "8px 0",
  },

  modalField: {
    marginBottom: 20,
  },

  modalLabel: {
    display: "block",
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 8,
    color: "#1f2937",
  },

  modalSelect: {
    width: "100%",
    padding: "10px 12px",
    fontSize: 14,
    border: "1px solid #e0e0e0",
    borderRadius: 12,
    outline: "none",
    background: "#fff",
  },

  modalTextarea: {
    width: "100%",
    padding: "10px 12px",
    fontSize: 14,
    border: "1px solid #e0e0e0",
    borderRadius: 12,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  modalFileInput: {
    width: "100%",
    padding: 8,
    fontSize: 14,
    border: "1px solid #e0e0e0",
    borderRadius: 12,
  },

  modalButton: {
    width: "100%",
    background: "#4f46e5",
    border: "none",
    borderRadius: 40,
    padding: 12,
    fontSize: 16,
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
  },

  // ==========================================================
  // SEND PKR MODAL STYLES
  // ==========================================================

  sendPkrModal: {
    padding: "0 0 8px 0",
    color: "#181818",
  },

  sendModalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px 12px",
    borderBottom: "1px solid #f0f0f0",
  },

  sendModalTitle: {
    fontSize: "18px",
    fontWeight: 700,
    margin: 0,
    color: "#1a1a1a",
  },

  sendCloseButton: {
    background: "none",
    border: "none",
    fontSize: "20px",
    cursor: "pointer",
    color: "#999",
    padding: "4px 8px",
  },

  sendBackButton: {
    background: "none",
    border: "none",
    fontSize: "22px",
    cursor: "pointer",
    color: "#666",
    padding: "4px 8px",
  },

  sendModalBody: {
    padding: "16px 20px 20px",
  },

  sendErrorBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#fff7ed",
    color: "#9a5b12",
    border: "1px solid #f0d2a4",
    borderRadius: "10px",
    padding: "10px 12px",
    marginBottom: "15px",
    fontSize: "12px",
  },

  sendBalanceCard: {
    background: "linear-gradient(135deg, #f9f5ed, #f4ead9)",
    borderRadius: "16px",
    padding: "14px 16px",
    marginBottom: "18px",
    border: "1px solid rgba(154,106,24,0.2)",
  },

  sendBalanceLabel: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#8a6a38",
    letterSpacing: "0.5px",
  },

  sendBalanceAmount: {
    fontSize: "26px",
    fontWeight: 800,
    color: "#28231c",
    margin: "4px 0 2px",
  },

  sendBalancePhone: {
    fontSize: "12px",
    opacity: 0.7,
    marginTop: "4px",
    color: "#6f5731",
  },

  sendFormSection: {
    marginBottom: "16px",
  },

  sendFormLabel: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    marginBottom: "6px",
  },

  sendInputRow: {
    display: "flex",
    gap: "8px",
  },

  sendPhoneInput: {
    flex: 1,
    height: "44px",
    padding: "0 12px",
    borderRadius: "10px",
    border: "1px solid #e0e0e0",
    fontSize: "14px",
    outline: "none",
    transition: "border-color 0.2s",
    background: "#fff",
  },

  sendResolveButton: {
    height: "44px",
    padding: "0 16px",
    borderRadius: "10px",
    border: "none",
    background: "linear-gradient(135deg, #9a6a18, #c89432)",
    color: "#fff",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "all 0.2s ease",
  },

  sendRecipientCard: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "12px 14px",
    background: "#f8fafc",
    borderRadius: "12px",
    border: "1px solid #e8e8e8",
    marginBottom: "16px",
  },

  sendRecipientAvatar: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    background: "#f4ead9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9a6a18",
  },

  sendAmountInput: {
    display: "flex",
    alignItems: "center",
    height: "50px",
    padding: "0 14px",
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    background: "#fff",
    transition: "border-color 0.2s",
  },

  sendAmountField: {
    flex: 1,
    border: "none",
    outline: "none",
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    background: "transparent",
    padding: 0,
  },

  sendAmountCurrency: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#666",
    marginLeft: "8px",
  },

  sendReviewButton: {
    width: "100%",
    height: "48px",
    borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg, #8d5f16, #b8862d)",
    color: "#fff",
    fontSize: "14px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginTop: "8px",
    boxShadow: "0 4px 14px rgba(154,106,24,0.2)",
  },

  sendConfirmCard: {
    background: "#f8fafc",
    borderRadius: "16px",
    padding: "20px",
    textAlign: "center",
    border: "1px solid #e8e8e8",
    marginBottom: "20px",
  },

  sendConfirmIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "#f4ead9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#9a6a18",
    margin: "0 auto 12px",
  },

  sendConfirmLabel: {
    fontSize: "12px",
    color: "#888",
    marginBottom: "4px",
  },

  sendConfirmName: {
    fontSize: "18px",
    fontWeight: 700,
    margin: "4px 0",
    color: "#1a1a1a",
  },

  sendConfirmPhone: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "12px",
  },

  sendConfirmAmount: {
    fontSize: "28px",
    fontWeight: 800,
    color: "#28231c",
    padding: "8px 0",
    borderTop: "1px solid #e8e8e8",
    borderBottom: "1px solid #e8e8e8",
  },

  sendRemainingBalance: {
    fontSize: "13px",
    color: "#666",
    marginTop: "12px",
  },

  sendButtonRow: {
    display: "flex",
    gap: "10px",
  },

  sendCancelButton: {
    flex: 1,
    height: "46px",
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    background: "#fff",
    color: "#666",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  sendConfirmButton: {
    flex: 2,
    height: "46px",
    borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg, #8d5f16, #b8862d)",
    color: "#fff",
    fontSize: "14px",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 14px rgba(154,106,24,0.2)",
  },

  sendSuccessHeader: {
    textAlign: "center",
    padding: "24px 20px 16px",
  },

  sendSuccessAmount: {
    textAlign: "center",
    fontSize: "32px",
    fontWeight: 800,
    color: "#28231c",
    padding: "8px 0",
  },

  sendSuccessCard: {
    background: "#f8fafc",
    borderRadius: "12px",
    padding: "16px",
    margin: "12px 20px",
    border: "1px solid #e8e8e8",
  },

  sendSuccessRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    fontSize: "13px",
    color: "#666",
    borderBottom: "1px solid #f0f0f0",
  },

  sendDoneButton: {
    width: "calc(100% - 40px)",
    margin: "16px 20px",
    height: "46px",
    borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg, #8d5f16, #b8862d)",
    color: "#fff",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 14px rgba(154,106,24,0.2)",
  },

  conversionToggle: {
    display: "flex",
    gap: "8px",
    marginBottom: "12px",
  },

  toggleButton: {
    flex: 1,
    padding: "8px 12px",
    border: "none",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  conversionBalanceRow: {
    display: "flex",
    alignItems: "center",
    padding: "10px 12px",
    marginBottom: "12px",
    background: "#f8fafc",
    borderRadius: "12px",
    border: "1px solid #e8e8e8",
  },

  conversionBalanceItem: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "2px",
  },

  conversionBalanceDivider: {
    width: "1px",
    height: "30px",
    background: "#e8e8e8",
  },
};

export default Dashboard;